'use client'

import { useState, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Loader2, Link as LinkIcon, Eye, EyeOff, Settings2, Copy, Check, ChevronDown } from 'lucide-react'
import { sanitizeDomain } from '@/lib/utils'
import type { ShopifyIntegrationError } from '@/hooks/integrations/use-shopify-integration'
import { DEFAULT_SCOPES } from '@/lib/integrations/oauth/shopify'
import { SHOPIFY_ADMIN_API_VERSION } from '@/lib/shopify/constants/api-version'

// Official-app OAuth connect deliberately has NO mode here: for Shopify-channel
// tenants the store is installed from the App Store (2.3.1 — no manual domain
// entry), and Stripe tenants connect data-only via their own custom app.
type ConnectMode = 'custom-app' | 'legacy'

interface AddShopifyIntegrationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCustomAppConnect: (data: {
    shop_domain: string
    custom_app_client_id: string
    custom_app_client_secret: string
  }) => Promise<boolean>
  onLegacyConnect: (data: {
    shop_domain: string
    uses_webhooks: boolean
    uses_api: boolean
    webhook_secret: string
    api_access_token: string
  }) => Promise<boolean>
  isConnecting: boolean
  error: ShopifyIntegrationError | null
  onClearError: () => void
  /** Comma-separated required scopes from system_settings (single source of truth) */
  requiredScopes?: string
}

const SCOPE_JUSTIFICATION =
  'The app analyzes full order history to calculate marketing attribution and long-term ROI per channel, requiring orders older than 60 days for accurate year-over-year and seasonal comparisons.'

const INITIAL_FORM_DATA = {
  shop_domain: '',
  uses_webhooks: false,
  uses_api: false,
  webhook_secret: '',
  api_access_token: '',
}

const INITIAL_CUSTOM_APP_DATA = {
  shop_domain: '',
  client_id: '',
  client_secret: '',
}

const CALLBACK_PATH = '/api/integrations/shopify/oauth/callback'

export function AddShopifyIntegrationDialog({
  open,
  onOpenChange,
  onCustomAppConnect,
  onLegacyConnect,
  isConnecting,
  error,
  onClearError,
  requiredScopes: requiredScopesProp,
}: AddShopifyIntegrationDialogProps) {
  const scopes = (requiredScopesProp || DEFAULT_SCOPES).split(',').map((s) => s.trim()).filter(Boolean)
  const [mode, setMode] = useState<ConnectMode>('custom-app')
  const [showToken, setShowToken] = useState(false)
  const [showCustomSecret, setShowCustomSecret] = useState(false)
  const [copiedScopes, setCopiedScopes] = useState(false)
  const [copiedUrl, setCopiedUrl] = useState(false)
  const [copiedJustification, setCopiedJustification] = useState(false)
  const [formData, setFormData] = useState(INITIAL_FORM_DATA)
  const [customAppData, setCustomAppData] = useState(INITIAL_CUSTOM_APP_DATA)

  const callbackUrl = typeof window !== 'undefined'
    ? `${window.location.origin}${CALLBACK_PATH}`
    : CALLBACK_PATH

  const resetDialog = useCallback(() => {
    setMode('custom-app')
    setShowToken(false)
    setShowCustomSecret(false)
    setCopiedScopes(false)
    setCopiedUrl(false)
    setCopiedJustification(false)
    setFormData(INITIAL_FORM_DATA)
    setCustomAppData(INITIAL_CUSTOM_APP_DATA)
    onClearError()
  }, [onClearError])

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) {
        resetDialog()
      }
      onOpenChange(newOpen)
    },
    [onOpenChange, resetDialog],
  )

  const handleCustomAppSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      const success = await onCustomAppConnect({
        shop_domain: sanitizeDomain(customAppData.shop_domain),
        custom_app_client_id: customAppData.client_id.trim(),
        custom_app_client_secret: customAppData.client_secret.trim(),
      })
      if (success) {
        handleOpenChange(false)
      }
    },
    [onCustomAppConnect, customAppData, handleOpenChange],
  )

  const handleLegacySubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      const sanitizedData = {
        ...formData,
        shop_domain: sanitizeDomain(formData.shop_domain),
      }
      const success = await onLegacyConnect(sanitizedData)
      if (success) {
        handleOpenChange(false)
      }
    },
    [formData, onLegacyConnect, handleOpenChange],
  )

  const copyToClipboard = useCallback(async (text: string, type: 'scopes' | 'url' | 'justification') => {
    await navigator.clipboard.writeText(text)
    const setters: Record<typeof type, (v: boolean) => void> = {
      scopes: setCopiedScopes,
      url: setCopiedUrl,
      justification: setCopiedJustification,
    }
    setters[type](true)
    setTimeout(() => setters[type](false), 2000)
  }, [])

  const switchMode = useCallback((newMode: ConnectMode) => {
    setMode(newMode)
    onClearError()
  }, [onClearError])

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Connect New Shopify Store</DialogTitle>
          <DialogDescription>
            <span>{mode === 'custom-app' && 'Connect using your own custom Shopify app credentials.'}</span>
            <span>{mode === 'legacy' && 'Manual setup for legacy custom apps (pre-January 2026).'}</span>
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        )}

        {/* ═══════════════ Custom App Connect (Primary) ═══════════════ */}
        {mode === 'custom-app' && (
          <form onSubmit={handleCustomAppSubmit} className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded p-3 space-y-2">
              <p className="text-xs text-blue-800 font-medium">Step 1 &mdash; Create the app</p>
              <ol className="text-xs text-blue-700 list-decimal list-inside space-y-1">
                <li><span>Go to</span> <strong>admin.shopify.com</strong> <span>&rarr; Apps &rarr; App settings &rarr;</span> <strong>Develop apps</strong> <span>&rarr;</span> <strong>Build apps in Dev Dashboard</strong></li>
                <li><span>Create app, name it, then create a new</span> <strong>Version</strong></li>
                <li><span>Set access scopes (copy from below) and API version</span> <strong>{SHOPIFY_ADMIN_API_VERSION}</strong><span>.</span> <em><span>Omit</span> <code>read_all_orders</code> <span>for now if it fails validation.</span></em></li>
                <li><span>Add the redirect URL below to</span> <strong>Allowed redirection URL(s)</strong></li>
                <li>Release the version and install it on your store</li>
                <li><span>Go to</span> <strong>Settings</strong> <span>and copy</span> <strong>Client ID</strong> <span>and</span> <strong>Client Secret</strong> <span>into the fields below</span></li>
              </ol>
            </div>

            <Collapsible>
              <div className="bg-amber-50 border border-amber-200 rounded p-3 space-y-2">
                <CollapsibleTrigger className="group flex items-center justify-between w-full text-left">
                  <div>
                    <p className="text-xs text-amber-800 font-medium">Step 2 &mdash; Enable full order history</p>
                    <p className="text-xs text-amber-600"><span>Only needed if</span> <code className="bg-amber-100 px-1 rounded">read_all_orders</code> <span>was rejected in Step 1. Shopify Plus stores usually get it automatically.</span></p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-amber-600 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-2 pt-1">
                  <ol className="text-xs text-amber-700 list-decimal list-inside space-y-1">
                    <li><span>Log in to</span> <a href="https://partners.shopify.com" target="_blank" rel="noopener noreferrer" className="underline font-medium">partners.shopify.com</a> <span>(create a free Partner account if you don&apos;t have one)</span></li>
                    <li><span>Find your app &rarr;</span> <strong>Choose distribution</strong> <span>&rarr;</span> <strong>Custom distribution</strong> <span>&rarr; enter your shop domain &rarr;</span> <strong>Generate link</strong> <span>(this unlocks the next step)</span></li>
                    <li><span>Go to</span> <strong>API access</strong> <span>&rarr;</span> <strong>Access requests</strong> <span>&rarr;</span> <strong>Read all orders scope</strong> <span>&rarr;</span> <strong>Request access</strong><span>. Paste this reason:</span></li>
                  </ol>
                  <div className="ml-4 flex items-start gap-2">
                    <p className="flex-1 text-xs italic bg-amber-100/60 rounded px-2 py-1.5">
                      {SCOPE_JUSTIFICATION}
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="shrink-0 mt-0.5"
                      onClick={() => copyToClipboard(SCOPE_JUSTIFICATION, 'justification')}
                    >
                      {copiedJustification ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                  <ol className="text-xs text-amber-700 list-decimal list-inside space-y-1" start={4}>
                    <li><span>Return to Dev Dashboard &rarr; your app &rarr;</span> <strong>Create version</strong> <span>&rarr; add</span> <code>read_all_orders</code> <span>to scopes &rarr;</span> <strong>Release</strong></li>
                  </ol>
                  <p className="text-xs text-amber-600 italic">
                    Note: Partner Dashboard is a separate system from Dev Dashboard. If you don&apos;t see your app there, contact support.
                  </p>
                </CollapsibleContent>
              </div>
            </Collapsible>

            {/* Required Scopes */}
            <div>
              <Label className="text-xs font-medium text-gray-700">Required Scopes</Label>
              <div className="mt-1 flex items-center gap-2">
                <code className="flex-1 text-xs bg-gray-100 rounded px-2 py-1.5 font-mono break-all">
                  {scopes.join(', ')}
                </code>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="shrink-0"
                  onClick={() => copyToClipboard(scopes.join(','), 'scopes')}
                >
                  {copiedScopes ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                </Button>
              </div>
            </div>

            {/* Redirect URL */}
            <div>
              <Label className="text-xs font-medium text-gray-700">Redirect URL</Label>
              <div className="mt-1 flex items-center gap-2">
                <code className="flex-1 text-xs bg-gray-100 rounded px-2 py-1.5 font-mono break-all">
                  {callbackUrl}
                </code>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="shrink-0"
                  onClick={() => copyToClipboard(callbackUrl, 'url')}
                >
                  {copiedUrl ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                </Button>
              </div>
            </div>

            <hr className="border-gray-200" />

            {/* Shop Domain */}
            <div>
              <Label htmlFor="custom-app-domain">Shop Domain *</Label>
              <Input
                id="custom-app-domain"
                value={customAppData.shop_domain}
                onChange={(e) => setCustomAppData({ ...customAppData, shop_domain: e.target.value })}
                placeholder="your-store.myshopify.com"
                required
                autoComplete="off"
              />
              <p className="text-xs text-gray-500 mt-1">
                <span>Use the canonical</span> <code className="font-mono">your-store.myshopify.com</code>{' '}
                <span>address, not a custom storefront URL like</span> <code className="font-mono">your-store.com</code><span>.
                Every Shopify store has a canonical</span> <code className="font-mono">.myshopify.com</code>{' '}
                <span>domain — find it in Shopify Admin →</span> <strong>Settings → Domains</strong><span>.</span>
              </p>
            </div>

            {/* Client ID */}
            <div>
              <Label htmlFor="custom-app-client-id">Client ID *</Label>
              <Input
                id="custom-app-client-id"
                value={customAppData.client_id}
                onChange={(e) => setCustomAppData({ ...customAppData, client_id: e.target.value })}
                placeholder="Your custom app Client ID"
                required
                autoComplete="off"
              />
            </div>

            {/* Client Secret */}
            <div>
              <Label htmlFor="custom-app-client-secret">Client Secret *</Label>
              <div className="relative">
                <Input
                  id="custom-app-client-secret"
                  type={showCustomSecret ? 'text' : 'password'}
                  value={customAppData.client_secret}
                  onChange={(e) => setCustomAppData({ ...customAppData, client_secret: e.target.value })}
                  placeholder="Your custom app Client Secret"
                  required
                  autoComplete="new-password"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowCustomSecret(!showCustomSecret)}
                >
                  {showCustomSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Your secret is encrypted before storage (AES-256-GCM).
              </p>
            </div>

            <div className="flex items-center gap-2 pt-2">
              <Button type="submit" disabled={isConnecting} className="bg-blue-600 hover:bg-blue-700">
                {isConnecting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    <span key="connecting">Connecting...</span>
                  </>
                ) : (
                  <>
                    <Settings2 className="w-4 h-4 mr-2" />
                    <span key="connect-custom-app">Connect Custom App</span>
                  </>
                )}
              </Button>
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
            </div>

            <div className="pt-1">
              <button
                type="button"
                onClick={() => switchMode('legacy')}
                className="text-xs text-gray-400 hover:text-gray-600 underline"
              >
                Legacy manual setup
              </button>
            </div>
          </form>
        )}

        {/* ═══════════════ Legacy Connect (Manual Token) ═══════════════ */}
        {mode === 'legacy' && (
          <form onSubmit={handleLegacySubmit} className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded p-3">
              <p className="text-xs text-amber-800">
                <strong>Legacy mode:</strong> <span>For Shopify custom apps created before January 2026. New stores should use the OAuth connection or custom app setup above.</span>
              </p>
            </div>

            <div>
              <Label htmlFor="shopify-domain">Shop Domain *</Label>
              <Input
                id="shopify-domain"
                value={formData.shop_domain}
                onChange={(e) => setFormData({ ...formData, shop_domain: e.target.value })}
                placeholder="your-store.myshopify.com"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                <span>Use the canonical</span> <code className="font-mono">your-store.myshopify.com</code>{' '}
                <span>address, not a custom storefront URL. Find it in Shopify Admin →</span>{' '}
                <strong>Settings → Domains</strong><span>.</span>
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="uses-webhooks"
                  checked={formData.uses_webhooks}
                  onCheckedChange={(checked) => setFormData({ ...formData, uses_webhooks: checked as boolean })}
                />
                <Label htmlFor="uses-webhooks">Enable Webhooks</Label>
              </div>

              {formData.uses_webhooks && (
                <div>
                  <Label htmlFor="webhook-secret">Webhook Secret *</Label>
                  <Input
                    id="webhook-secret"
                    value={formData.webhook_secret}
                    onChange={(e) => setFormData({ ...formData, webhook_secret: e.target.value })}
                    placeholder="Your webhook signing secret"
                    required={formData.uses_webhooks}
                  />
                </div>
              )}

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="uses-api"
                  checked={formData.uses_api}
                  onCheckedChange={(checked) => setFormData({ ...formData, uses_api: checked as boolean })}
                />
                <Label htmlFor="uses-api">Enable API Access</Label>
              </div>

              {formData.uses_api && (
                <div>
                  <Label htmlFor="api-access-token">API Access Token *</Label>
                  <div className="relative">
                    <Input
                      id="api-access-token"
                      type={showToken ? 'text' : 'password'}
                      value={formData.api_access_token}
                      onChange={(e) => setFormData({ ...formData, api_access_token: e.target.value })}
                      placeholder="Your Shopify Admin API access token"
                      required={formData.uses_api}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowToken(!showToken)}
                    >
                      {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 pt-2">
              <Button type="submit" disabled={isConnecting}>
                {isConnecting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    <span key="connecting">Connecting...</span>
                  </>
                ) : (
                  <>
                    <LinkIcon className="w-4 h-4 mr-2" />
                    <span key="connect-legacy">Connect (Legacy)</span>
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  switchMode('custom-app')
                  setFormData(INITIAL_FORM_DATA)
                }}
              >
                Back
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
